import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { ChainType, PickingChainEntry } from '../models/PickingChainEntry';
import {
  ProductOption,
  searchProductOptions,
  searchExternalProductOptions,
} from '../services/PickingChainCRUD';

interface SelectOption {
  value: number;
  label: string;
}

export interface PickingChainFormPayload {
  chain_type: ChainType;
  position: number;
  product: number | null;
  external_product: number | null;
  zone_code: string;
}

interface FormValues {
  chain_type: ChainType;
  position: number;
  product: number | null;
  external_product: number | null;
  zone_code: string;
}

interface PickingChainFormModalProps {
  visible: boolean;
  entry: PickingChainEntry | null;
  onClose: () => void;
  onSubmit: (values: PickingChainFormPayload) => Promise<void>;
}

const toOptions = (items: ProductOption[]): SelectOption[] =>
  items.map((item) => ({
    value: item.id,
    label: `${item.ean} - ${item.name}`,
  }));

function getInitialValues(entry: PickingChainEntry | null): FormValues {
  return {
    chain_type: entry?.chain_type || 'main',
    position: entry?.position || 1,
    product: entry?.product || null,
    external_product: entry?.external_product || null,
    zone_code: entry?.zone_code || '',
  };
}

export function PickingChainFormModal({
  visible,
  entry,
  onClose,
  onSubmit,
}: PickingChainFormModalProps) {
  const intl = useIntl();
  const isEditing = !!entry;

  const [values, setValues] = useState<FormValues>(getInitialValues(entry));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [productOptions, setProductOptions] = useState<SelectOption[]>([]);
  const [extProductOptions, setExtProductOptions] = useState<SelectOption[]>(
    []
  );
  const [loadingProductOptions, setLoadingProductOptions] = useState(false);
  const [loadingExtProductOptions, setLoadingExtProductOptions] =
    useState(false);

  const currentOption: SelectOption | null = (() => {
    if (!entry) return null;
    const id =
      entry.chain_type === 'main' ? entry.product : entry.external_product;
    if (!id) return null;
    return { value: id, label: `${entry.ean} - ${entry.product_name}` };
  })();

  async function loadProductOpts(search: string = '') {
    setLoadingProductOptions(true);
    try {
      const { data } = await searchProductOptions(search);
      setProductOptions(toOptions(data));
    } finally {
      setLoadingProductOptions(false);
    }
  }

  async function loadExtProductOpts(search: string = '') {
    setLoadingExtProductOptions(true);
    try {
      const { data } = await searchExternalProductOptions(search);
      setExtProductOptions(toOptions(data));
    } finally {
      setLoadingExtProductOptions(false);
    }
  }

  const mergedProductOptions = (() => {
    if (!currentOption || entry?.chain_type !== 'main') return productOptions;
    if (productOptions.some((o) => o.value === currentOption.value))
      return productOptions;
    return [currentOption, ...productOptions];
  })();

  const mergedExtProductOptions = (() => {
    if (!currentOption || entry?.chain_type !== 'bvs') return extProductOptions;
    if (extProductOptions.some((o) => o.value === currentOption.value))
      return extProductOptions;
    return [currentOption, ...extProductOptions];
  })();

  useEffect(() => {
    if (visible) {
      setValues(getInitialValues(entry));
      setErrors({});
      if ((entry?.chain_type || 'main') === 'main') {
        loadProductOpts();
      } else {
        loadExtProductOpts();
      }
    }
  }, [visible, entry]);

  // Clean opposite product field when chain_type changes
  useEffect(() => {
    if (values.chain_type === 'main') {
      setValues((prev) => ({ ...prev, external_product: null }));
      loadProductOpts();
    } else {
      setValues((prev) => ({ ...prev, product: null }));
      loadExtProductOpts();
    }
  }, [values.chain_type]); // eslint-disable-line react-hooks/exhaustive-deps

  function setField<K extends keyof FormValues>(
    field: K,
    value: FormValues[K]
  ) {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (values.position < 1) {
      newErrors.position = intl.formatMessage({
        id: 'pickingChain.positionRequired',
      });
    }
    if (values.chain_type === 'main' && !values.product) {
      newErrors.product = intl.formatMessage({
        id: 'pickingChain.productRequired',
      });
    }
    if (values.chain_type === 'bvs' && !values.external_product) {
      newErrors.external_product = intl.formatMessage({
        id: 'pickingChain.productRequired',
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const payload: PickingChainFormPayload = {
      chain_type: values.chain_type,
      position: values.position,
      zone_code: values.zone_code,
      product: values.chain_type === 'main' ? values.product : null,
      external_product:
        values.chain_type === 'bvs' ? values.external_product : null,
    };

    try {
      setSubmitting(true);
      await onSubmit(payload);
      handleClose();
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    setValues(getInitialValues(null));
    setErrors({});
    onClose();
  }

  return (
    <Modal show={visible} onHide={handleClose} backdrop="static" centered>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage({
              id: isEditing
                ? 'pickingChain.editEntry'
                : 'pickingChain.createEntry',
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-5">
            <label className="form-label fw-bold required">
              {intl.formatMessage({ id: 'pickingChain.chainType' })}
            </label>
            <select
              className="form-select"
              value={values.chain_type}
              onChange={(e) =>
                setField('chain_type', e.target.value as ChainType)
              }
              disabled={isEditing}
            >
              <option value="main">
                {intl.formatMessage({ id: 'pickingChain.main' })}
              </option>
              <option value="bvs">
                {intl.formatMessage({ id: 'pickingChain.bvs' })}
              </option>
            </select>
          </div>

          <div className="mb-5">
            <label className="form-label fw-bold required">
              {intl.formatMessage({ id: 'pickingChain.position' })}
            </label>
            <input
              type="number"
              className="form-control"
              min={1}
              required
              value={values.position}
              onChange={(e) => setField('position', Number(e.target.value))}
            />
            {errors.position && (
              <div className="text-danger mt-1">{errors.position}</div>
            )}
          </div>

          {values.chain_type === 'main' && (
            <div className="mb-5">
              <label className="form-label fw-bold required">
                {intl.formatMessage({ id: 'pickingChain.product' })}
              </label>
              <Select
                options={mergedProductOptions}
                value={
                  mergedProductOptions.find(
                    (opt) => opt.value === values.product
                  ) || null
                }
                onChange={(option) =>
                  setField('product', option?.value || null)
                }
                onInputChange={(input) => {
                  if (input.length >= 2) loadProductOpts(input);
                }}
                isLoading={loadingProductOptions}
                isClearable
                placeholder={intl.formatMessage({
                  id: 'pickingChain.searchProduct',
                })}
              />
              {errors.product && (
                <div className="text-danger mt-1">{errors.product}</div>
              )}
            </div>
          )}

          {values.chain_type === 'bvs' && (
            <>
              <div className="mb-5">
                <label className="form-label fw-bold required">
                  {intl.formatMessage({ id: 'pickingChain.product' })}
                </label>
                <Select
                  options={mergedExtProductOptions}
                  value={
                    mergedExtProductOptions.find(
                      (opt) => opt.value === values.external_product
                    ) || null
                  }
                  onChange={(option) =>
                    setField('external_product', option?.value || null)
                  }
                  onInputChange={(input) => {
                    if (input.length >= 2) loadExtProductOpts(input);
                  }}
                  isLoading={loadingExtProductOptions}
                  isClearable
                  placeholder={intl.formatMessage({
                    id: 'pickingChain.searchProduct',
                  })}
                />
                {errors.external_product && (
                  <div className="text-danger mt-1">
                    {errors.external_product}
                  </div>
                )}
              </div>
              <div className="mb-5">
                <label className="form-label fw-bold">
                  {intl.formatMessage({ id: 'pickingChain.zoneCode' })}
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={values.zone_code}
                  onChange={(e) => setField('zone_code', e.target.value)}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            {intl.formatMessage({ id: 'common.cancel' })}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting && (
              <span className="spinner-border spinner-border-sm me-2" />
            )}
            {intl.formatMessage({ id: 'common.save' })}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
